import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Package } from '@models/package';
import { ClinicServiceTemplate } from '@models/service/clinic-service-template';
import { CatalogueUpdatesService } from '@services/catalogueupdates.service';
import { ClinicsService } from '@services/clinics.service';
import { PackagesService } from '@services/packages.service';
import { ServiceTemplatesService } from '@services/service-templates.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-edit-package',
  templateUrl: './edit-package.component.html',
  styleUrls: ['./edit-package.component.less'],
})
export class EditPackageComponent implements OnInit, OnDestroy {
  packageForm: FormGroup;
  addOrEdit = 'Add';
  package: Package;
  filteredServiceTemplates: ClinicServiceTemplate[] = [];
  private allServiceTemplates: ClinicServiceTemplate[] = [];
  private unsub: Subject<void> = new Subject<void>();

  get priceDetails(): string {
    const template = this.packageForm.get('serviceTemplate')?.value as ClinicServiceTemplate;
    const servicePrice = template?.defaultPrice;
    const quantity = this.packageForm.get('quantity')?.value as number;
    const pkgPrice = this.packageForm.get('price')?.value as number;
    const calculatedPrice = servicePrice * quantity;

    const servicePriceStr = isNaN(servicePrice) ? 'Service Price' : this.currencyPipe.transform(servicePrice);
    const quantityStr = isNaN(quantity) || quantity <= 0 ? 'Quantity' : quantity;
    const calculatedPriceStr =
      isNaN(calculatedPrice) || isNaN(servicePrice) || quantity <= 0
        ? 'Total'
        : this.currencyPipe.transform(calculatedPrice);
    const pkgPriceStr = this.currencyPipe.transform(pkgPrice);
    const totalStr = pkgPrice ? `<s>${calculatedPriceStr}</s> ${pkgPriceStr}` : calculatedPriceStr;

    return `${servicePriceStr} x ${quantityStr} = ${totalStr}`;
  }

  constructor(
    private packagesService: PackagesService,
    private clinicsService: ClinicsService,
    private serviceTemplatesService: ServiceTemplatesService,
    private catalogueUpdatesService: CatalogueUpdatesService,
    private route: ActivatedRoute,
    private router: Router,
    private currencyPipe: CurrencyPipe
  ) {
    this.packageForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      quantity: new FormControl(null, [Validators.required, Validators.min(2)]),
      serviceTemplate: new FormControl(null, [Validators.required]),
      price: new FormControl(null, [Validators.min(0)]),
      note: new FormControl(''),
    });

    this.package = {
      id: 0,
      name: '',
      note: '',
      quantity: 0,
      serviceTemplateId: 0,
      serviceTemplate: null,
      price: null,
      clinicId: null,
    };
  }

  ngOnInit() {
    this.serviceTemplatesService.getServiceTemplateMultiples().subscribe((st) => {
      this.allServiceTemplates = st;
      this.filteredServiceTemplates = st;
    });

    this.route.params.pipe(takeUntil(this.unsub)).subscribe((params) => {
      const id = parseInt(params['pkg-id']);
      if (Number.isInteger(id) && id > 0) {
        this.addOrEdit = 'Edit';
        this.packagesService.getPackage(id).subscribe((pkg) => {
          this.package = pkg;
          this.packageForm.patchValue(pkg);
        });
      }
    });
  }

  onServiceSearchKey(event: KeyboardEvent) {
    const value = (event.target as HTMLInputElement).value;
    const searchTerms = value.split(' ');
    this.filteredServiceTemplates = this.allServiceTemplates.filter((service) => {
      var name = service.serviceName.toLowerCase();
      let match = true;
      for (var term of searchTerms) {
        if (!name.includes(term.toLowerCase())) {
          match = false;
          break;
        }
      }
      if (match) return true;
    });
  }

  compareServiceTemplates(serv1: ClinicServiceTemplate, serv2: ClinicServiceTemplate): boolean {
    return serv1 && serv2 && serv1.id === serv2.id;
  }

  serviceDisplayName(serv: ClinicServiceTemplate): string | undefined {
    return serv?.serviceName;
  }

  updatePackage() {
    this.package.name = this.packageForm.get('name').value;
    this.package.quantity = this.packageForm.get('quantity').value;
    this.package.price = this.packageForm.get('price').value;
    this.package.serviceTemplateId = this.packageForm.get('serviceTemplate').value.id;
    this.package.note = this.packageForm.get('note').value;

    if (this.package.id === 0) {
      this.package.clinicId = this.clinicsService.clinic.clinicId;
      this.packagesService.addPackage(this.package).subscribe(() => {
        this.catalogueUpdatesService.refreshRequired = true;
        this.catalogueUpdatesService.catalogueUpdateComplete();
        this.router.navigate(['/management/catalogue/packages', { outlets: { 'action-panel': null } }]);
      });
    } else {
      this.packagesService.updatePackage(this.package).subscribe(() => {
        this.catalogueUpdatesService.refreshRequired = true;
        this.catalogueUpdatesService.catalogueUpdateComplete();
        this.router.navigate(['/management/catalogue/packages', { outlets: { 'action-panel': null } }]);
      });
    }
  }

  cancelUpdate() {
    this.catalogueUpdatesService.refreshRequired = false;
    this.catalogueUpdatesService.catalogueUpdateComplete();
    this.router.navigate(['/management/catalogue/packages', { outlets: { 'action-panel': null } }]);
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
